import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { Label } from '../Label';
import { uniqueId } from 'lodash';
import './styles.scss';
import { DateDetails } from '../models/DateDetails';
import { InputType, Variant } from '../models';
import { InputPropsBase } from '../interfaces/Inputs';
import TsrSvgIcon, { Icon } from '../../assets/icons';
import { InputContainer } from '../InputContainer';
import TsrTooltip from '../../tooltip';
import Calendar from 'react-calendar';
import { DateMask } from '../Text/DateMask';
import { TooltipType } from '../models/TooltipType';
import { Button } from '../../buttons';
import { JustifyContents } from '../../layout';
import { LOCALE } from '../../Locales/locales';
import { enCommonTranslations, plCommonTranslations } from '../../Locales/commonTranslations';
import { DateTime } from 'luxon';
import { Value } from 'react-calendar/dist/shared/types';

export interface DateProps extends InputPropsBase<string> {
    showArrows?: boolean;
    format?: string;
    gap?: number;
    maxDate?: DateTime;
    minDate?: DateTime;
    maxDetails?: DateDetails;
    variant?: Variant;
    time?: boolean;
    invalidDateMessage?: string;
    locale: string;
    disableDateRangeValidationInDateMask?: boolean;
    inputType?: InputType;
    setIsValueValid?: (isValueValid: boolean) => void;
}

export const DateInput = forwardRef<HTMLInputElement, DateProps>((props, ref) => {
    const defaultInvalidDateMessage = 'Date is invalid';
    const generatedId = useMemo(() => uniqueId('idDate-'), []);
    const inputId = props.id || generatedId;
    const format =
        props.format ||
        (props.maxDetails === DateDetails.Year
            ? 'MM-yyyy'
            : props.time
            ? 'dd-MM-yyyy HH:mm'
            : props.maxDetails === DateDetails.Decade
            ? 'yyyy'
            : 'dd-MM-yyyy');

    const [date, setDate] = useState(props.value);
    const [lastCorrectDate, setLastCorrectDate] = useState(props.value);
    const [dateHasBeenChangedAlgorithmically, setDateHasBeenChangedAlgorithmically] = useState(false);
    const [calendarToogle, setCalendarToogle] = useState(false);
    const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return;
        }
        if (!date) {
            props.onBlur && props.onBlur('');
            return;
        }

        const parsed = DateTime.fromFormat(date, format);
        if (parsed.isValid) {
            const normalized = parsed.toFormat(format);
            if (normalized !== date) {
                setDate(normalized);
                setDateHasBeenChangedAlgorithmically(true);
            }
            props.onBlur && props.onBlur(normalized);
            return;
        }

        setDate(lastCorrectDate);
        setDateHasBeenChangedAlgorithmically(true);
        props.onChange && props.onChange(lastCorrectDate);
        props.setIsValueValid && props.setIsValueValid(DateTime.fromFormat(lastCorrectDate, format).isValid);
        props.onBlur && props.onBlur(lastCorrectDate);
    };
    useEffect(() => {
        if (!props.value) {
            setDate('');
            props.setIsValueValid && props.setIsValueValid(false);
        } else if (DateTime.fromFormat(props.value, format).isValid || undefined) {
            setDate(props.value);
            setLastCorrectDate(props.value);
            props.setIsValueValid && props.setIsValueValid(true);
        }
    }, [format, props.value]);

    const isDateNotEmpty = (): boolean => {
        return !!(date || props.value);
    };

    const isDateInvalid = (): boolean => {
        return !DateTime.fromFormat(date || props.value, format).isValid;
    };

    // react-calendar works on local-midnight JS Dates; the app sets luxon Settings.defaultZone = 'UTC', so convert in 'local'.
    const onChangeDate = (value: Value) => {
        const newDate: string = DateTime.fromJSDate(value as Date, { zone: 'local' }).toFormat(format);
        if (lastCorrectDate === newDate && lastCorrectDate !== date) {
            setDateHasBeenChangedAlgorithmically(true);
        }
        setCalendarToogle(false);
        setLastCorrectDate(newDate);
        setDate(newDate);
        props.onChange && props.onChange(newDate);
        props.setIsValueValid && props.setIsValueValid(true);
    };

    const onChangeInput = (value: string) => {
        setDate(value);
        if (value === '') {
            if (props.inputType === InputType.DateToolbar) {
                setDate(lastCorrectDate);
                props.onChange && props.onChange(lastCorrectDate);
                setDateHasBeenChangedAlgorithmically(true);
                props.setIsValueValid && props.setIsValueValid(true);
            } else {
                props.onChange && props.onChange('');
                props.setIsValueValid && props.setIsValueValid(false);
            }
        } else {
            if (DateTime.fromFormat(value, format).isValid) {
                setLastCorrectDate(DateTime.fromFormat(value, format).toFormat(format));
                props.onChange && props.onChange(DateTime.fromFormat(value, format).toFormat(format));
                props.setIsValueValid && props.setIsValueValid(true);
            } else {
                props.setIsValueValid && props.setIsValueValid(false);
            }
        }
    };

    const interval = { step: 1, type: 'days' };
    const dateArrows = (type: boolean) => {
        if (props.disabled === 'inputOnly' || props.disabled === true) {
            return;
        }
        if (!DateTime.fromFormat(props.value, format).isValid) {
            return;
        }
        switch (props.maxDetails) {
            case DateDetails.Century:
                interval.type = 'years';
                interval.step = 10;
                break;
            case DateDetails.Decade:
                interval.type = 'years';
                interval.step = 1;
                break;
            case DateDetails.Year:
                interval.type = 'months';
                interval.step = 1;
                break;
            default:
                interval.type = 'days';
                interval.step = 1;
                break;
        }
        if (type) {
            let addedDate = DateTime.fromFormat(props.value, format);
            addedDate = addedDate.plus({ [interval.type]: interval.step });
            props.onChange && props.onChange(addedDate.toFormat(format));
        } else {
            let subtractDate = DateTime.fromFormat(props.value, format);
            subtractDate = subtractDate.minus({ [interval.type]: interval.step });
            props.onChange && props.onChange(subtractDate.toFormat(format));
        }
    };

    function arrowPreviousRender(shouldRender: boolean) {
        if (shouldRender) {
            return (
                <TsrSvgIcon
                    icon="doubleArrowPrevious"
                    color={props.disabled === 'inputOnly' || props.disabled === true ? 'disabled' : 'info'}
                    onClick={() => dateArrows(true)}
                    pointer
                />
            );
        }
    }
    function arrowNextRender(shouldRender: boolean) {
        if (shouldRender) {
            return (
                <TsrSvgIcon
                    icon="doubleArrowNext"
                    color={props.disabled === 'inputOnly' || props.disabled === true ? 'disabled' : 'info'}
                    onClick={() => dateArrows(false)}
                    pointer
                />
            );
        }
    }
    return (
        <InputContainer
            gap={props.gap}
            justify={props.justify}
            align={props.align}
            className={`${props.className} ${props.variant}`}
            overflow={props.overflow}
            height="auto"
            width={props.width}
            inputContainerLength={props.inputContainerLength}
        >
            <Label
                id={inputId}
                content={props.label}
                disabled={props.disabled === true}
                labelPosition={props.labelPosition}
                labelLength={props.labelLength || 1}
                className={props.labelClassName}
            >
                <div
                    style={{ flex: props.inputLength || 1, width: props.inputFieldWidth }}
                    className={props.disabled ? 'disabled tsr-input-date' : 'tsr-input-date'}
                    onBlur={onBlur}
                >
                    {arrowPreviousRender(props.showArrows || false)}
                    <DateMask
                        id={inputId}
                        value={date}
                        min={props.minDate}
                        tabIndex={props.tabIndex}
                        max={props.maxDate}
                        disableDateRangeValidation={props.disableDateRangeValidationInDateMask}
                        maxDetails={props.maxDetails}
                        className={props.variant === Variant.AgGrid ? 'date-text-input ignoreOutsideClick' : 'date-text-input'}
                        disabled={props.disabled === 'inputOnly' || props.disabled === true}
                        onChange={onChangeInput}
                        time={props.time}
                        ref={ref}
                        dateHasBeenChangedAlgorithmically={dateHasBeenChangedAlgorithmically}
                        setDateHasBeenChangedAlgorithmically={setDateHasBeenChangedAlgorithmically}
                    >
                        {isDateInvalid() && isDateNotEmpty() && (
                            <TsrTooltip
                                type={TooltipType.Error}
                                icon={true}
                                content={props.invalidDateMessage || defaultInvalidDateMessage}
                            >
                                <TsrSvgIcon icon="information" color="error" pointer />
                            </TsrTooltip>
                        )}
                        {props.errorMessage && !props.disabled ? (
                            <TsrTooltip
                                type={TooltipType.Error}
                                icon={true}
                                content={props.errorMessage}
                                disabled={props.hideErrorTooltip}
                            >
                                <TsrSvgIcon icon="information" color="error" pointer />
                            </TsrTooltip>
                        ) : props.infoMessage && !props.disabled ? (
                            <TsrTooltip icon={true} content={props.infoMessage}>
                                <TsrSvgIcon icon="information" color="info" pointer />
                            </TsrTooltip>
                        ) : (
                            props.infoMessage && <TsrSvgIcon icon="information" color="disabled" />
                        )}
                        {calendarToogle && (
                            <TsrTooltip
                                type={TooltipType.Controls}
                                interactive
                                showOnCreate
                                appendTo={document.body}
                                disabled={props.disabled === 'inputOnly' || props.disabled === true}
                                trigger="click"
                                className="tippy-borderless"
                                onAfterUpdate={instance => !calendarToogle && instance.hide()}
                                content={
                                    <div className="calendar">
                                        <Calendar
                                            maxDetail={props.maxDetails}
                                            minDate={props.minDate?.toJSDate()}
                                            maxDate={props.maxDate?.toJSDate()}
                                            className={
                                                props.variant === Variant.AgGrid ? 'date-input ignoreOutsideClick' : 'date-input'
                                            }
                                            value={
                                                DateTime.fromFormat(date, format).isValid
                                                    ? DateTime.fromFormat(date, format, { zone: 'local' }).toJSDate()
                                                    : undefined
                                            }
                                            onChange={onChangeDate}
                                            nextLabel={<TsrSvgIcon icon={Icon.ArrowNext} />}
                                            next2Label={<TsrSvgIcon icon={Icon.DoubleArrowNext} />}
                                            prevLabel={<TsrSvgIcon icon={Icon.ArrowPrevious} />}
                                            prev2Label={<TsrSvgIcon icon={Icon.DoubleArrowPrevious} />}
                                            locale={props.locale}
                                        />
                                        <Button
                                            text={
                                                props.locale === LOCALE.polish
                                                    ? plCommonTranslations.now
                                                    : enCommonTranslations.now
                                            }
                                            icon={Icon.CalendarDay}
                                            onClick={() => {
                                                onChangeDate(new Date());
                                            }}
                                            theme="primary"
                                            className={props.variant === Variant.AgGrid ? 'ignoreOutsideClick' : ''}
                                            justify={JustifyContents.flexEnd}
                                        />
                                    </div>
                                }
                            />
                        )}
                        <TsrSvgIcon
                            icon="calendar"
                            color={props.disabled === 'inputOnly' || props.disabled === true ? 'disabled' : 'info'}
                            pointer
                            onClick={() =>
                                !(props.disabled === 'inputOnly' || props.disabled === true) && setCalendarToogle(!calendarToogle)
                            }
                        />
                    </DateMask>

                    {arrowNextRender(props.showArrows || false)}
                </div>
            </Label>
        </InputContainer>
    );
});
